/**
 * Obstruction Filter Selectors
 *
 * Atomic + derived selectors for the obstruction filter slice. Mirrors
 * `taskFilterSelectors.ts` / `noteSelectors` (filter half) — used by the
 * artifact registry to decide whether to light up the "has active filters"
 * dot on the artifact bar.
 */
import type { GeoMarkupStore } from '../index';
import type { ObstructionFiltersParams } from '@/types/api/requests';
import { buildObstructionFiltersParams } from '../slices/obstructionFiltersSlice';

// ===== ATOMIC FILTER SELECTORS =====

/** Full obstruction filter slice (object — wrap in `useShallow` when subscribing). */
export const selectObstructionFilters = (state: GeoMarkupStore) =>
  state.obstructionFilters;

/** Obstruction search-text filter. */
export const selectObstructionFilterSearch = (state: GeoMarkupStore): string =>
  state.obstructionFilters.search;

// ===== DERIVED FILTER SELECTORS =====

/**
 * Whether any obstruction filter is active (including search).
 */
export const selectHasActiveObstructionFilters = (
  state: GeoMarkupStore
): boolean => {
  const {
    search,
    types,
    statuses,
    severities,
    impacts,
    location,
    contractor,
    dateFrom,
    dateTo,
  } = state.obstructionFilters;
  return !!(
    search ||
    types.length > 0 ||
    statuses.length > 0 ||
    severities.length > 0 ||
    impacts.length > 0 ||
    location ||
    contractor ||
    dateFrom !== null ||
    dateTo !== null
  );
};

/**
 * Excludes `search` for parity with `selectHasActiveTaskFiltersInMapPanel` —
 * on the map view the SearchBar lives outside the collapsible Filters panel
 * so it must not light up the filters-dot.
 */
export const selectHasActiveObstructionFiltersInMapPanel = (
  state: GeoMarkupStore
): boolean => {
  const {
    types,
    statuses,
    severities,
    impacts,
    location,
    contractor,
    dateFrom,
    dateTo,
  } = state.obstructionFilters;
  return !!(
    types.length > 0 ||
    statuses.length > 0 ||
    severities.length > 0 ||
    impacts.length > 0 ||
    location ||
    contractor ||
    dateFrom !== null ||
    dateTo !== null
  );
};

/**
 * Serialised key of obstruction filter state (suitable as `useEffect`
 * dependency). Includes all filters but not pagination — fetch effects
 * watch this to trigger a refetch on filter change.
 */
export const selectObstructionFilterKey = (state: GeoMarkupStore): string => {
  const {
    search,
    types,
    statuses,
    severities,
    impacts,
    location,
    contractor,
    dateFrom,
    dateTo,
    sortBy,
    sortDir,
  } = state.obstructionFilters;
  return [
    search,
    types.join(','),
    statuses.join(','),
    severities.join(','),
    impacts.join(','),
    location,
    contractor,
    dateFrom?.getTime() ?? '',
    dateTo?.getTime() ?? '',
    sortBy,
    sortDir,
  ].join('|');
};

/**
 * Project the active obstruction filter slice to API params. Centralised so
 * fetch hooks don't reach into `buildObstructionFiltersParams` directly —
 * cleaner unit-test boundary and easier mocking.
 */
export const selectObstructionFilterParams = (
  state: GeoMarkupStore
): ObstructionFiltersParams => {
  return buildObstructionFiltersParams(state.obstructionFilters);
};

/**
 * Discriminated chip descriptor for the active obstruction filters. The
 * filters panel renders one chip per descriptor with an X-to-remove button.
 *
 * Identifiers (`kind`/`value`) align with filter slice setters so the
 * remove-button handler can dispatch directly.
 */
export type ObstructionFilterChip =
  | { kind: 'search'; label: string }
  | { kind: 'type'; value: string; label: string }
  | { kind: 'status'; value: string; label: string }
  | { kind: 'severity'; value: string; label: string }
  | { kind: 'impact'; value: string; label: string }
  | { kind: 'location'; label: string }
  | { kind: 'contractor'; label: string }
  | { kind: 'dateRange'; from: Date | null; to: Date | null; label: string };

/**
 * Build the active-filter chip list. The label-translation lives in
 * the consuming React component — this selector only returns the raw
 * shapes so it can run outside React (e.g. in tests).
 */
export const selectActiveObstructionFilterChips = (
  state: GeoMarkupStore
): ObstructionFilterChip[] => {
  const {
    search,
    types,
    statuses,
    severities,
    impacts,
    location,
    contractor,
    dateFrom,
    dateTo,
  } = state.obstructionFilters;

  const chips: ObstructionFilterChip[] = [];

  if (search) {
    chips.push({ kind: 'search', label: search });
  }
  for (const type of types) {
    chips.push({ kind: 'type', value: type, label: type });
  }
  for (const status of statuses) {
    chips.push({ kind: 'status', value: status, label: status });
  }
  for (const severity of severities) {
    chips.push({ kind: 'severity', value: severity, label: severity });
  }
  for (const impact of impacts) {
    chips.push({ kind: 'impact', value: impact, label: impact });
  }
  if (location) {
    chips.push({ kind: 'location', label: location });
  }
  if (contractor) {
    chips.push({ kind: 'contractor', label: contractor });
  }
  if (dateFrom !== null || dateTo !== null) {
    chips.push({
      kind: 'dateRange',
      from: dateFrom,
      to: dateTo,
      label: 'dateRange',
    });
  }

  return chips;
};
